import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import type { RegionalPoolEmployeeTenantAgreementResponse } from 'generated';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import type { InferType } from 'yup';

import { mutations, queries, queryKeys } from 'RestAPIs/rapini';

import { LoadingButton } from '@mui/lab';
import {
  Button,
  DialogActions,
  FormControlLabel,
  Switch,
  TextField
} from '@mui/material';

import {
  ControlledDatePicker,
  Dialog,
  showSnackbar,
  VirtualizedAutocomplete
} from 'components/shared';
import { optionPadding } from 'components/shared/Autocomplete/MuiAutocompleteOption';

import regionalPoolEmploymentOrganisationsSchema from './regional-pool-employment-organisations-schema';
import {
  GridContainer,
  GridCompact6
} from 'components/shared/Grid/Grid.components';

const schema = regionalPoolEmploymentOrganisationsSchema();
type Form = InferType<typeof schema>;

const initialValues = {
  tenant: {},
  validFrom: dayjs().startOf('day'),
  validTo: dayjs().startOf('year').add(1, 'year'),
  allowDay: true,
  allowNight: true
};

export type Props = {
  dialogContainer?: HTMLElement | null;
  open: boolean;
  handleClose: () => void;
  employeeId: number;
  employmentId: number;
  agreementToEdit?: RegionalPoolEmployeeTenantAgreementResponse | null;
};

export default function RegionalPoolEmploymentOrganisationsDialog({
  dialogContainer,
  open,
  handleClose,
  employeeId,
  employmentId,
  agreementToEdit
}: Props) {
  'use memo';
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const { data } = queries.useGetRegionalPoolTenants();

  const { control, handleSubmit, reset } = useForm<Form>({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if (agreementToEdit) {
      reset({
        tenant: agreementToEdit.tenant,
        validFrom: dayjs(agreementToEdit?.validFrom),
        validTo: dayjs(agreementToEdit?.validTo),
        allowDay: agreementToEdit.allowDay,
        allowNight: agreementToEdit.allowNight
      });
    } else {
      reset(initialValues);
    }
  }, [agreementToEdit, reset]);

  const { mutate: save, isLoading: saveLoading } =
    mutations.usePostRegionalPoolEmployeeTenantAgreements({
      onError: (e: any) => {
        if (e?.response?.data?.error?.message === 'invalid.function') {
          showSnackbar({
            severity: 'error',
            message: t('error.invalid.function')
          });
        } else {
          showSnackbar({
            severity: 'error',
            message: t('somethingWentWrong')
          });
        }
        handleClose();
      },
      onSuccess: () => {
        handleClose();
        queryClient.invalidateQueries(
          queryKeys.getRegionalPoolEmployeeTenantAgreements(employmentId)
        );
      }
    });

  const { mutate: update, isLoading: updateLoading } =
    mutations.usePutRegionalPoolEmployeeTenantAgreementsId(
      agreementToEdit?.id as number,
      {
        onError: () => {
          showSnackbar({
            severity: 'error',
            message: t('somethingWentWrong')
          });
          handleClose();
        },
        onSuccess: () => {
          handleClose();
          queryClient.invalidateQueries(
            queryKeys.getRegionalPoolEmployeeTenantAgreements(employmentId)
          );
        }
      }
    );

  function onSubmit(data: Form) {
    const { tenant, validFrom, validTo, allowDay, allowNight } = data;

    const payload = {
      employeeId,
      employmentId,
      tenantId: tenant.tenantName,
      validFrom: validFrom.format('YYYY-MM-DD'),
      validTo: validTo.format('YYYY-MM-DD'),
      allowDay,
      allowNight
    };

    if (agreementToEdit) {
      update({
        id: agreementToEdit.id,
        ...payload
      });
    } else {
      save(payload);
    }

    handleClose();
  }

  const isLoading = saveLoading || updateLoading;

  return (
    <Dialog
      open={open}
      title={t(
        agreementToEdit ? 'editRequestOrganization' : 'addRequestOrganization'
      )}
      maxWidth="extralarge"
      handleClose={handleClose}
      container={dialogContainer}
    >
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="tenant"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <VirtualizedAutocomplete
                value={field.value}
                onChange={(e, v) => {
                  field.onChange(v);
                }}
                options={data ?? []}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      ref={params.InputProps.ref}
                      label={t('organization')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message ?? null}
                      required
                    />
                  );
                }}
                optionLabelProp="displayName"
                isOptionEqualToValue={(option, value) => {
                  return option?.tenantName === value?.tenantName;
                }}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  return option?.displayName ?? '';
                }}
                autoHighlight
                extraWidth={optionPadding * 2}
                disabled={!!agreementToEdit}
              />
            );
          }}
        />

        <br />

        <GridContainer>
          <GridCompact6>
            <ControlledDatePicker
              control={control}
              name="validFrom"
              label={t('validFrom')}
              disabled={
                !!agreementToEdit &&
                new Date(agreementToEdit?.validFrom || '1970-01-01') <
                  new Date()
              }
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledDatePicker
              control={control}
              name="validTo"
              label={t('validTo')}
            />
          </GridCompact6>
          <GridCompact6>
            <Controller
              name="allowDay"
              control={control}
              render={({ field }) => {
                return (
                  <FormControlLabel
                    label={t(`allowDay`)}
                    control={
                      <Switch
                        size="small"
                        value={field.value}
                        checked={field.value}
                        onChange={(e, v) => {
                          field.onChange(v);
                        }}
                      />
                    }
                  />
                );
              }}
            />
          </GridCompact6>
          <GridCompact6>
            <Controller
              name="allowNight"
              control={control}
              render={({ field }) => {
                return (
                  <FormControlLabel
                    label={t(`allowNight`)}
                    control={
                      <Switch
                        size="small"
                        value={field.value}
                        checked={field.value}
                        onChange={(e, v) => {
                          field.onChange(v);
                        }}
                      />
                    }
                  />
                );
              }}
            />
          </GridCompact6>
        </GridContainer>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="contained">
            {t('cancel')}
          </Button>
          <LoadingButton
            loading={isLoading}
            type="submit"
            color="primary"
            variant="contained"
          >
            {t('save')}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
